/**
 * Central route constants (CLAUDE.md: "always use the ROUTES object, never hardcode paths").
 *
 * Introduced with the QS Report v2 full-page document route; existing routes migrate here
 * opportunistically as they are touched. Builders return concrete paths for navigation;
 * `*Pattern` entries are the react-router path patterns AppRoutes registers.
 */
import { SLACK_TWINS_CONNECT_PATH } from '@digital-twins/shared-types'

export const ROUTES = {
  // Standard study-creation entry — the goal grid.
  testsNew: '/tests/new',
  // Home Lab prototype routes. See docs/plans/new-experience-plan.md.
  // Step 11 (docs/plans/assistant-mvp-plan.md): `/` now mounts HomeLabPage. `homeLab` stays
  // as an additional path to the same page so existing links/muscle memory keep working.
  home: '/',
  homeLab: '/home-lab',
  // The pre-Step-11 homepage, kept reachable as a fallback while Home Lab is finished.
  // Never delete this route or HomePage while HomeLabPage is still incomplete.
  homeClassic: '/home-classic',
  assistantLab: '/assistant-lab',
  // Assistant MVP Step 6 (docs/plans/assistant-mvp-plan.md). `/chats/:id` is registered
  // at AppRoutes.tsx but had no ROUTES entry — Step 3's job registry parked the `chat`
  // job's handoff href on `ROUTES.homeLab` as a knowingly-wrong placeholder rather than
  // hardcode a path (CLAUDE.md). This is the real builder; Step 7 repoints to it.
  chatRoom: (token: string) => `/chats/${encodeURIComponent(token)}`,
  // Knowledge threads lobby and a single thread. Direct focus-group plan Step 7
  // (docs/plans/home-lab-direct-focus-group.md): `/threads` and `/threads/:token` were
  // hardcoded at AppRoutes.tsx, the same violation a concurrent session already fixed for
  // `/knowledge` below. `thread` is the builder data-mode submit navigates to.
  threads: '/threads',
  thread: (token: string) => `/threads/${encodeURIComponent(token)}`,
  // Knowledge library hub and its upload entry point.
  knowledge: '/knowledge',
  knowledgeUpload: '/knowledge/upload',
  // A single study's results page, registered at AppRoutes.tsx as `/tests/:id/results`.
  // Home Lab composer plan Step 3 (docs/plans/home-lab-composer-language-and-recent-study.md):
  // added for the recent-study line's link and used there only. The path is hardcoded at
  // several existing call sites; per this file's own header, routes migrate here
  // opportunistically as they are touched, so those are deliberately NOT swept in this change.
  studyResults: (id: string) => `/tests/${encodeURIComponent(id)}/results`,
  // Assistant Platform v1 Step 6.1 (docs/plans/assistant-platform-v1-execution-plan.md):
  // read-only workflows lobby + detail. Keyed by the template `slug`, never the row id.
  workflows: '/workflows',
  workflow: (slug: string) => `/workflows/${encodeURIComponent(slug)}`,
  // Assistant Platform v1 Step 5.1: the sidebar's nav array was converted to ROUTES, and
  // these three lobbies had no constant to convert to. Added here rather than left as
  // literals, because a half-converted array is worse than an unconverted one: the next
  // person copying a row cannot tell which form is correct. Other call sites still hardcode
  // these paths and migrate opportunistically, per this file's header.
  tests: '/tests',
  chats: '/chats',
  audiences: '/audiences',
  // A single audience, keyed by its public token (the room pane's audience banner).
  // The Audiences cards still hardcode `/audiences/${id}`; switching them over is a separate cleanup.
  audience: (id: string) => `/audiences/${encodeURIComponent(id)}`,
  // Assistant Platform v1 Step 3.2 (docs/plans/assistant-platform-v1-execution-plan.md):
  // the Settings page's tabs are all query-param routes off one page, and 27 call sites
  // across 17 files had hardcoded the `/settings?tab=...` literal directly (CLAUDE.md
  // violation). This is the one builder for all of them, including the new `skills` tab.
  settingsTab: (tab: string) => `/settings?tab=${encodeURIComponent(tab)}`,
  // Workflow skill chips deep-link onto the matching Skills tab row (D19).
  settingsSkill: (slug: string) =>
    `/settings?tab=skills&skill=${encodeURIComponent(slug)}`,
  // Slack Twins account linking. The path lives in
  // shared-types because the backend builds the connect URL Twins posts in Slack.
  slackConnect: SLACK_TWINS_CONNECT_PATH,
  // Login page; takes `?returnTo=<path>` (see LoginPage, safeReturnTo).
  login: '/login',
  // Fixture-only ChatGPT artifact chrome bed (Edit · Copy · Expand). Internal.
  ARTIFACT_PREVIEW_BED: '/internal/artifact-preview-bed',
  // Discardable StudyDoc print letterhead bed (logo + Made with {org} Twins).
  STUDY_DOC_PRINT_PREVIEW: '/internal/study-doc-print-preview',
} as const

/**
 * Query the sidebar Ask CTA sets so Home Lab starts a fresh conversation even
 * when the user is already on `/`. A link to `ROUTES.home` is a no-op there.
 */
export const NEW_ASK_PARAM = 'new'
export const NEW_ASK_PARAM_VALUE = '1'

export function homeNewAskHref(): string {
  return `${ROUTES.home}?${NEW_ASK_PARAM}=${NEW_ASK_PARAM_VALUE}`
}
